import { gql, useMutation, useQuery } from "@apollo/client";
import React, { useMemo } from "react";
import { Manager } from "../../../../schema";
import Avatar from "../../../../ui/Avatar";
import PrimaryButton from "../../../../ui/PrimaryButton";
import TextButton from "../../../../ui/TextButton";
import Modal from "../../../../ui/modal/Modal";
import ModalButtons from "../../../../ui/modal/ModalButtons";
import ModalContent from "../../../../ui/modal/ModalContent";
import ModalTitle from "../../../../ui/modal/ModalTitle";
import { reverse } from "../../../../urls";
import { handleError, handleSuccess } from "../../../../utils/Apollo";
import UnexpectedErrorScene from "../../../system/UnexpectedErrorScene";

type LicensesTab = {
  licenseeId: string;
};

function ManagerRow({
  licenseeId,
  manager,
}: {
  licenseeId: string;
  manager: Manager;
}) {
  const [showDelete, setShowDelete] = React.useState(false);

  const [deleteManager] = useMutation(deleteMutation, {
    refetchQueries: ["ManagersQuery"],
  });

  const onClose = () => {
    setShowDelete(false);
  };

  const companyId = licenseeId.replace("cmp_", "");
  const onDelete = React.useCallback(() => {
    deleteManager({
      variables: { id: manager.id, companyId },
    }).then(
      () => {
        setShowDelete(false);
        handleSuccess("Manager was deleted");
      },
      (apolloError) => handleError(apolloError)
    );
  }, []);

  return (
    <tr
      className="border-b last:border-b-0 border-gray-light hover:bg-blue-section"
      key={manager.id}
    >
      <td className="flex p-3">
        <Avatar
          name={manager.name}
          url={manager.avatar ? manager.avatar : undefined}
          className="w-6 h-6 rounded-full mr-2 -mt-0.5"
        />
        {manager.name}
      </td>
      <td className="pl-4 ">{manager.email}</td>
      <td className="">
        <div className="gap-x-4 flex items-center">
          <a
            href={reverse("licensee_managers_edit", {
              id: companyId,
              managerId: manager.id,
            })}
            className="text-blue hover:underline"
          >
            Edit
          </a>
          <button
            className="text-blue hover:underline"
            onClick={() => setShowDelete(true)}
          >
            Delete
          </button>
        </div>
      </td>
      <Modal show={showDelete} onClose={onClose}>
        <ModalTitle title={"Delete company manager"} onClose={onClose} />
        <ModalContent>
          <p>Are you sure you want to proceed?</p>
        </ModalContent>
        <ModalButtons>
          <PrimaryButton onClick={onDelete}>Delete</PrimaryButton>
          <TextButton onClick={onClose}>Cancel</TextButton>
        </ModalButtons>
      </Modal>
    </tr>
  );
}

export function ManagersTab({ licenseeId }: LicensesTab) {
  const { loading, data, error } = useQuery<ManagersQuery>(query, {
    variables: { id: licenseeId },
  });

  const managers = useMemo(() => {
    const unsorted: Manager[] = [];
    data?.managers.forEach((type) => unsorted.push(type));
    return unsorted.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  if (error) {
    return (
      <UnexpectedErrorScene error={error} errorInfo={null} eventId={null} />
    );
  }

  const companyId = licenseeId.replace("cmp_", "");

  return (
    <div className="mt-8 flex flex-row">
      <div className="flex flex-col w-1/2">
        {!loading && !!data && (
          <>
            <table>
              <thead>
                <tr className="mb-4 border-b border-gray-light">
                  <th className="text-left pl-4 py-3">Manager</th>
                  <th className="text-left pl-4 py-3">Email</th>
                  <th>
                    <a
                      href={reverse("licensee_managers_add", {
                        id: companyId,
                      })}
                      className="text-blue hover:underline text-normal"
                    >
                      + Add
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                {managers.map((manager) => (
                  <ManagerRow
                    licenseeId={licenseeId}
                    manager={manager}
                    key={manager.id}
                  />
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

interface ManagersQuery {
  managers: Manager[];
}

const query = gql`
  query ManagersQuery($id: ID!) {
    managers: licenseeManagers(licenseeId: $id) {
      id
      email
      name
      avatar
    }
  }
`;

const deleteMutation = gql`
  mutation DeleteManager($id: ID!, $companyId: ID!) {
    deleteCompanyManager(id: $id, companyId: $companyId) {
      id
    }
  }
`;
